/**
 * DynamicRequestDetails
 *
 * Generic, declarative READ view for a request type's saved `requestTypeDetails`.
 * The mirror of DynamicRequestFields: a request type added to the registry as a
 * `fields` array renders here automatically (label-above-value grid), with no new
 * component. Custom types (e.g. IO) provide their own `DetailsView` instead.
 *
 * Field descriptor is the same one DynamicRequestFields uses; values are formatted
 * by `formatFieldValue` (dates, currency, percent, ranges, selects, switches).
 */

import React from 'react';
import PropTypes from 'prop-types';
import Text from 'antd/lib/typography/Text';
import { formatFieldValue, hasValue } from './fieldHelpers';

// One grid cell: label stacked above its value. `full` spans the whole row (URLs / long text).
export const DetailCell = ({ label, value, isLink, full }) => {
  if (!hasValue(value)) return null;
  return (
    <div className={`task-rtd__cell${full ? ' task-rtd__cell--full' : ''}`}>
      <span className="task-rtd__cell-label">{label}</span>
      {isLink ? (
        <a className="task-rtd__cell-value" href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      ) : (
        <span className="task-rtd__cell-value">{value}</span>
      )}
    </div>
  );
};

DetailCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLink: PropTypes.bool,
  full: PropTypes.bool,
};

DetailCell.defaultProps = { value: null, isLink: false, full: false };

// URLs and free text get their own full-width row; everything else flows in the grid.
const isFullWidth = (field) => field.type === 'url' || field.type === 'textarea';

const DynamicRequestDetails = ({ fields, details, title }) => {
  const visible = (fields || []).filter((f) => hasValue(details ? details[f.name] : undefined));
  if (visible.length === 0) return null;

  return (
    <div className="task-rtd">
      <Text className="task-rtd__title">{title}</Text>
      <div className="task-rtd__grid">
        {visible.map((field) => (
          <DetailCell
            key={field.name}
            label={field.label}
            value={formatFieldValue(field, details[field.name])}
            isLink={field.type === 'url'}
            full={isFullWidth(field)}
          />
        ))}
      </div>
    </div>
  );
};

DynamicRequestDetails.propTypes = {
  fields: PropTypes.array,
  details: PropTypes.object,
  title: PropTypes.string,
};

DynamicRequestDetails.defaultProps = {
  fields: [],
  details: null,
  title: 'REQUEST DETAILS',
};

export default DynamicRequestDetails;
